import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AlertTriangle, Check, Eye, EyeOff, FileText, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BulkEnvEditor } from "@/components/BulkEnvEditor";
import { CustomEnvironmentsSection } from "@/components/CustomEnvironments";
import { LinkedEnvGroups } from "@/components/LinkedEnvGroups";

type EnvName = "production" | "staging" | "preview" | "development";
const ENVS: EnvName[] = ["production", "staging", "preview", "development"];

interface EnvVar {
  id: string;
  key: string;
  environment: EnvName;
  isSecret: boolean;
  updatedAt: string;
  valueMasked?: string;
  customEnvironmentId?: string | null;
}

export function Environments() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [filterEnv, setFilterEnv] = useState<EnvName | "all">("all");
  const [revealedValues, setRevealedValues] = useState<Record<string, string>>({});
  const [reauthOpen, setReauthOpen] = useState(false);
  // What to re-run once the reauth prompt succeeds (a row reveal or the bulk
  // editor load).
  const [pendingReauth, setPendingReauth] = useState<(() => void) | null>(null);
  // Bulk .env editor — replaces the row list for the selected environment.
  const [bulkEditing, setBulkEditing] = useState(false);
  const [bulkDirty, setBulkDirty] = useState(false);
  const selectEnv = (next: EnvName | "all") => {
    if (next === filterEnv) return;
    if (bulkEditing && bulkDirty && !confirm("Discard unsaved changes?")) return;
    if (next === "all") setBulkEditing(false);
    setFilterEnv(next);
  };

  const vars = useQuery({
    queryKey: ["env-vars", org, project],
    queryFn: () =>
      api
        .get<{ vars: EnvVar[] }>(`/orgs/${org}/projects/${project}/env-vars`)
        .then((r) => r.vars),
  });
  // Custom env names, for the per-row scope chip (006 US6).
  const customEnvs = useQuery({
    queryKey: ["custom-environments", org, project],
    queryFn: () =>
      api
        .get<{ environments: Array<{ id: string; name: string }> }>(`/orgs/${org}/projects/${project}/environments`)
        .then((r) => r.environments),
  });
  const envNameById = useMemo(
    () => new Map((customEnvs.data ?? []).map((e) => [e.id, e.name])),
    [customEnvs.data],
  );

  const filtered = useMemo(() => {
    if (!vars.data) return [];
    if (filterEnv === "all") return vars.data;
    return vars.data.filter((v) => v.environment === filterEnv);
  }, [vars.data, filterEnv]);

  const reveal = async (id: string) => {
    try {
      const r = await api.post<{ key: string; value: string }>(
        `/orgs/${org}/projects/${project}/env-vars/${id}/reveal`,
        undefined,
        { headers: { "x-reauth": "yes" } },
      );
      setRevealedValues((m) => ({ ...m, [id]: r.value }));
    } catch (e) {
      if (e instanceof ApiError && e.code === "reauth_required") {
        setPendingReauth(() => () => void reveal(id));
        setReauthOpen(true);
      } else {
        throw e;
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="of-card">
        <div className="of-card-header gap-3">
          <h3 className="text-sm font-semibold shrink-0">Environment variables</h3>
          <div className="flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-none">
            <button
              onClick={() => selectEnv("all")}
              className={pillClass(filterEnv === "all")}
            >
              all
            </button>
            {ENVS.map((e) => (
              <button key={e} onClick={() => selectEnv(e)} className={pillClass(filterEnv === e)}>
                {e}
              </button>
            ))}
          </div>
          {!bulkEditing && (
            <Button
              variant="default"
              size="sm"
              className="ml-auto shrink-0"
              onClick={() => setBulkEditing(true)}
              disabled={filterEnv === "all"}
              title={
                filterEnv === "all"
                  ? "Pick an environment to edit its variables as .env text"
                  : `Edit all ${filterEnv} variables as .env text (requires reauth)`
              }
            >
              <FileText className="h-3.5 w-3.5" /> Edit as .env
            </Button>
          )}
        </div>

        {bulkEditing && filterEnv !== "all" && (
          <BulkEnvEditor
            key={filterEnv}
            orgSlug={org!}
            projectSlug={project!}
            environment={filterEnv}
            hasCustomOverrides={filtered.some((v) => v.customEnvironmentId)}
            requestReauth={(retry) => { setPendingReauth(() => retry); setReauthOpen(true); }}
            onDirtyChange={setBulkDirty}
            onClose={() => {
              if (bulkDirty && !confirm("Discard unsaved changes?")) return;
              setBulkEditing(false);
            }}
          />
        )}

        {!bulkEditing && vars.isLoading && <Empty>Loading…</Empty>}
        {!bulkEditing && !vars.isLoading && filtered.length === 0 && (
          <Empty>
            {filterEnv === "all"
              ? "No env vars yet."
              : <>No vars scoped to <span className="font-mono">{filterEnv}</span>.</>}
          </Empty>
        )}

        {!bulkEditing && filtered.map((v) => (
          <div key={v.id} className="of-row gap-3 items-start min-h-[44px]">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold truncate">{v.key}</span>
                {v.isSecret && (
                  <span className="text-2xs uppercase tracking-wider bg-attention-subtle text-attention border border-attention/30 rounded px-1.5 py-0.5">
                    secret
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                <span className="text-2xs uppercase tracking-wider bg-subtle border border-border-muted rounded px-1.5 py-0.5 font-mono">
                  {v.environment}
                </span>
                {v.customEnvironmentId && (
                  <span className="text-2xs uppercase tracking-wider bg-accent-subtle text-accent border border-accent/30 rounded px-1.5 py-0.5 font-mono">
                    env:{envNameById.get(v.customEnvironmentId) ?? "?"}
                  </span>
                )}
              </div>
              <div className="mt-1.5 font-mono text-xs">
                {revealedValues[v.id] ? (
                  <span className="text-foreground break-all">{revealedValues[v.id]}</span>
                ) : (
                  <span className="text-subtle-foreground">{v.valueMasked ?? "••••••••"}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 shrink-0">
              {revealedValues[v.id] ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRevealedValues((m) => { const n = { ...m }; delete n[v.id]; return n; })}
                  title="Hide value"
                >
                  <EyeOff className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => reveal(v.id)} title="Reveal (requires reauth)">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              )}
              <DeleteButton
                onConfirm={async () => {
                  await api.delete(`/orgs/${org}/projects/${project}/env-vars/${v.id}`);
                  qc.invalidateQueries({ queryKey: ["env-vars", org, project] });
                  qc.invalidateQueries({ queryKey: ["project", org, project] });
                }}
                label={v.key}
              />
            </div>
          </div>
        ))}
      </div>

      <CustomEnvironmentsSection orgSlug={org!} projectSlug={project!} />
      <LinkedEnvGroups orgSlug={org!} projectSlug={project!} />
      <AddVarForm orgSlug={org!} projectSlug={project!} customEnvs={customEnvs.data ?? []} />
      <BulkPasteForm orgSlug={org!} projectSlug={project!} />

      {reauthOpen && (
        <ReauthModal
          onClose={() => { setReauthOpen(false); setPendingReauth(null); }}
          onAuthed={() => { setReauthOpen(false); pendingReauth?.(); setPendingReauth(null); }}
        />
      )}
    </div>
  );
}

function AddVarForm({
  orgSlug,
  projectSlug,
  customEnvs,
}: {
  orgSlug: string;
  projectSlug: string;
  customEnvs: Array<{ id: string; name: string }>;
}) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [envs, setEnvs] = useState<EnvName[]>(["production", "staging", "preview", "development"]);
  const [isSecret, setIsSecret] = useState(true);
  // "" = base value; else custom environment id (scoped override, 006 US6).
  const [scope, setScope] = useState("");
  // When the user pastes a multi-var blob into either input, expand it
  // into individual rows instead of stuffing the whole thing into one
  // value. null = normal single-var entry mode; non-null = multi-add mode.
  const [pendingBulk, setPendingBulk] = useState<{ key: string; value: string }[] | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; warning?: string }>(`/orgs/${orgSlug}/projects/${projectSlug}/env-vars`, {
        key, value, environments: envs, isSecret,
        ...(scope ? { customEnvironmentId: scope } : {}),
      }),
    onSuccess: () => {
      setKey(""); setValue("");
      qc.invalidateQueries({ queryKey: ["env-vars", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["project", orgSlug, projectSlug] });
    },
  });

  const bulkApply = useMutation({
    mutationFn: () =>
      api.post<{ applied: number; warnings: string[] }>(
        `/orgs/${orgSlug}/projects/${projectSlug}/env-vars/bulk`,
        {
          mode: "apply",
          vars: (pendingBulk ?? []).map((p) => ({ ...p, environments: envs, isSecret })),
        },
      ),
    onSuccess: () => {
      setPendingBulk(null);
      qc.invalidateQueries({ queryKey: ["env-vars", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["project", orgSlug, projectSlug] });
    },
  });

  // Paste handler: parse the clipboard as .env. Intercept only when it
  // actually looks like env content. Field-specific rules:
  //   - KEY field: 1 entry → autofill both fields (convenience for the
  //     common "I copied KEY=val" case); 2+ entries → multi-add mode.
  //   - VALUE field: only intercept for 2+ entries — a single entry could
  //     be a base64 value with a trailing `=` that we don't want to
  //     mis-parse as KEY=.
  const onPasteKey = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const parsed = parseEnvBlob(e.clipboardData.getData("text"));
    if (parsed.length === 0) return;
    e.preventDefault();
    if (parsed.length === 1) {
      setKey(parsed[0].key);
      setValue(parsed[0].value);
    } else {
      setPendingBulk(parsed);
      setKey(""); setValue("");
    }
  };
  const onPasteValue = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const parsed = parseEnvBlob(e.clipboardData.getData("text"));
    if (parsed.length < 2) return;
    e.preventDefault();
    setPendingBulk(parsed);
    setKey(""); setValue("");
  };

  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add variable
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        {pendingBulk ? (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">
              Detected <span className="font-semibold">{pendingBulk.length}</span> variables in your paste. Comments stripped.
            </div>
            <div className="border border-border-muted rounded-md max-h-60 overflow-auto divide-y divide-border-muted">
              {pendingBulk.map((p, i) => (
                <div key={`${p.key}-${i}`} className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                  <div className="min-w-0 flex-1 truncate font-mono text-xs">
                    <span className="font-semibold">{p.key}</span>
                    <span className="text-subtle-foreground"> = </span>
                    <span className="text-subtle-foreground truncate">
                      {p.value.length > 40 ? `${p.value.slice(0, 40)}…` : p.value || "(empty)"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-danger"
                    onClick={() => setPendingBulk((cur) => {
                      if (!cur) return cur;
                      const next = cur.filter((_, j) => j !== i);
                      return next.length === 0 ? null : next;
                    })}
                    aria-label={`Remove ${p.key}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2">
            <Input value={key} onChange={(e) => setKey(e.target.value)} onPaste={onPasteKey} placeholder="KEY" className="font-mono text-sm" />
            <Input value={value} onChange={(e) => setValue(e.target.value)} onPaste={onPasteValue} placeholder="value" className="font-mono text-sm" />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {ENVS.map((e) => (
            <label key={e} className="text-xs flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={envs.includes(e)}
                onChange={(ev) => setEnvs((cur) => ev.target.checked ? Array.from(new Set([...cur, e])) : cur.filter((x) => x !== e))}
              />
              <span className="font-mono">{e}</span>
            </label>
          ))}
          {customEnvs.length > 0 && (
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="h-7 px-2 rounded-md border border-border bg-background text-xs font-mono"
              title="Scope: base value or a custom-environment override"
            >
              <option value="">base value</option>
              {customEnvs.map((ce) => (
                <option key={ce.id} value={ce.id}>override for env:{ce.name}</option>
              ))}
            </select>
          )}
          <label className="text-xs flex items-center gap-1 cursor-pointer ml-auto">
            <input type="checkbox" checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} />
            secret
          </label>
        </div>
        {create.data?.warning && <Warning text={create.data.warning} />}
        {create.error instanceof ApiError && (
          <div className="text-xs text-danger">{create.error.message}</div>
        )}
        {bulkApply.error instanceof ApiError && (
          <div className="text-xs text-danger">{bulkApply.error.message}</div>
        )}
        {bulkApply.data?.warnings.map((w) => <Warning key={w} text={w} />)}
        <div className="flex justify-end gap-2">
          {pendingBulk && (
            <Button variant="default" size="sm" onClick={() => setPendingBulk(null)} disabled={bulkApply.isPending}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => pendingBulk ? bulkApply.mutate() : create.mutate()}
            disabled={
              pendingBulk
                ? pendingBulk.length === 0 || envs.length === 0 || bulkApply.isPending
                : !key || !value || envs.length === 0 || create.isPending
            }
          >
            {(create.isPending || bulkApply.isPending) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            {pendingBulk ? `Add ${pendingBulk.length} variable${pendingBulk.length === 1 ? "" : "s"}` : "Add"}
          </Button>
        </div>
      </div>
    </details>
  );
}

function BulkPasteForm({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const [blob, setBlob] = useState("");
  const [envs, setEnvs] = useState<EnvName[]>(["production", "staging", "preview", "development"]);

  // Lazy preview: only fires when user clicks Preview.
  const preview = useMutation({
    mutationFn: () => {
      const parsed = parseEnvBlob(blob);
      return api.post<{ diff: { key: string; status: string; warning?: string }[] }>(
        `/orgs/${orgSlug}/projects/${projectSlug}/env-vars/bulk`,
        { mode: "preview", vars: parsed.map((p) => ({ ...p, environments: envs, isSecret: true })) },
      );
    },
  });
  const apply = useMutation({
    mutationFn: () => {
      const parsed = parseEnvBlob(blob);
      return api.post<{ applied: number; warnings: string[] }>(
        `/orgs/${orgSlug}/projects/${projectSlug}/env-vars/bulk`,
        { mode: "apply", vars: parsed.map((p) => ({ ...p, environments: envs, isSecret: true })) },
      );
    },
    onSuccess: () => {
      setBlob("");
      qc.invalidateQueries({ queryKey: ["env-vars", orgSlug, projectSlug] });
      qc.invalidateQueries({ queryKey: ["project", orgSlug, projectSlug] });
    },
  });

  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Upload className="h-3.5 w-3.5" /> Bulk paste .env
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <Label htmlFor="bulk-blob" className="text-xs">Paste .env contents</Label>
        <textarea
          id="bulk-blob"
          value={blob}
          onChange={(e) => setBlob(e.target.value)}
          spellCheck={false}
          placeholder={"DATABASE_URL=postgres://…\nAPI_KEY=sk-…"}
          className="w-full min-h-[120px] font-mono text-xs bg-subtle border border-border rounded-md p-2.5"
        />
        <div className="flex flex-wrap items-center gap-2">
          {ENVS.map((e) => (
            <label key={e} className="text-xs flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={envs.includes(e)}
                onChange={(ev) => setEnvs((cur) => ev.target.checked ? Array.from(new Set([...cur, e])) : cur.filter((x) => x !== e))}
              />
              <span className="font-mono">{e}</span>
            </label>
          ))}
        </div>
        {preview.data && (
          <div className="text-xs space-y-1 max-h-40 overflow-auto border border-border-muted rounded p-2">
            {preview.data.diff.map((d) => (
              <div key={d.key} className="flex justify-between gap-2 font-mono">
                <span className="min-w-0 truncate">{d.key}</span>
                <span className={d.status === "add" ? "text-success" : d.status === "update" ? "text-attention" : "text-subtle-foreground"}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
        )}
        {apply.data && (
          <div className="text-xs text-success">{apply.data.applied} variables saved.</div>
        )}
        {apply.data?.warnings.map((w) => <Warning key={w} text={w} />)}
        <div className="flex justify-end gap-2">
          <Button variant="default" size="sm" onClick={() => preview.mutate()} disabled={!blob || preview.isPending}>
            {preview.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Preview diff
          </Button>
          <Button variant="primary" size="sm" onClick={() => apply.mutate()} disabled={!blob || apply.isPending}>
            {apply.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Apply
          </Button>
        </div>
      </div>
    </details>
  );
}

function ReauthModal({ onClose, onAuthed }: { onClose: () => void; onAuthed: () => void }) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="of-card w-[calc(100vw-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
        <div className="of-card-header"><h3 className="text-sm font-semibold">Re-enter your password</h3></div>
        <form
          className="px-4 py-3 space-y-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true); setErr(null);
            try {
              await api.post("/auth/reauth", { password });
              onAuthed();
            } catch (e) {
              setErr(e instanceof ApiError ? e.message : "reauth failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          <Label htmlFor="reauth-pw" className="text-xs">Password</Label>
          <Input id="reauth-pw" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
          {err && <div className="text-xs text-danger">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="default" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={!password || busy}>
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteButton({ onConfirm, label }: { onConfirm: () => Promise<void>; label: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={async () => {
        if (!confirm(`Delete ${label}?`)) return;
        setBusy(true);
        try { await onConfirm(); } finally { setBusy(false); }
      }}
      disabled={busy}
      title="Delete"
      className="text-danger hover:bg-danger-subtle"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
    </Button>
  );
}

function Warning({ text }: { text: string }) {
  return (
    <div className="text-xs text-attention bg-attention-subtle border border-attention/30 rounded-md px-2.5 py-1.5 inline-flex items-start gap-1.5">
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}

function pillClass(active: boolean): string {
  return (
    "text-2xs uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 whitespace-nowrap " +
    (active
      ? "bg-foreground text-background border-foreground"
      : "bg-subtle border-border-muted hover:bg-[rgb(var(--of-canvas-inset))]")
  );
}

export function parseEnvBlob(text: string): { key: string; value: string }[] {
  // Normalize: when an env blob is pasted without real linebreaks (e.g.,
  // copied from a browser render where newlines became spaces), insert a
  // newline before every embedded KEY= so the line-by-line parser still
  // splits correctly. Without this every embedded var gets absorbed into
  // the value of the first one.
  const normalized = text.replace(
    /([^\n])(\s+)([A-Z_][A-Z0-9_]*\s*=)/g,
    (_m, prev, _ws, kv) => `${prev}\n${kv}`,
  );
  const out: { key: string; value: string }[] = [];
  for (const raw of normalized.split(/\r?\n/)) {
    const line = raw.replace(/^\s*export\s+/, "").trim();
    if (!line || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    const key = line.slice(0, eq).trim();
    if (!/^[A-Z_][A-Z0-9_]*$/i.test(key)) continue;
    let value = line.slice(eq + 1);
    // Strip inline `# comment` from unquoted values (matches ` #` so `#`
    // inside URLs and JSON-no-space stays intact). Conventional .env file
    // behaviour; matches dotenv / docker-compose semantics.
    const startsQuoted = /^\s*['"]/.test(value);
    if (!startsQuoted) {
      const commentIdx = value.search(/\s+#/);
      if (commentIdx !== -1) value = value.slice(0, commentIdx);
    }
    value = value.trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    out.push({ key, value });
  }
  return out;
}
